import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild
       } from '@angular/core';

import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { TemplateConfig } from 'collage-designer/collage-designer-config';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { StageService } from 'collage-designer/stage/stage.service';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { BoxLarge } from './renderer/box-large';
import { BoxLarge2023 } from './renderer/box-large-2023';
import { BoxSmall } from './renderer/box-small';
import { BoxSmallUsa2021 } from './renderer/box-small-usa-2021';

@Component({
    selector: 'preview',
    template: `
    <div class="wrapper">
      <div #spinner class="spinner">
        <mat-spinner color="accent"></mat-spinner>
      </div>
      <canvas #canvas></canvas>
    </div>
  `,
    styleUrls: ['./preview.component.scss'],
    standalone: false
})
export class PreviewComponent implements AfterViewInit, OnDestroy
{
  public static readonly TRY_RESTART_TIMEOUT: number = 250; /* ms */
  public static PREVIEW_OPTIONS: any = {
      grabMoveEnabled: false,
      rotationEnabled: true,
      rotationStep: 0.8,
      autoUpdateEnabled: false,
  };
  private static RENDERER_CLASSES: any = {
      'box-large': BoxLarge,
      'box-large-2023': BoxLarge2023,
      'box-small': BoxSmall,
      'box-small-usa-2021': BoxSmallUsa2021,
  };
  private static MAX_SPINNER_TIME: number = 10000; /* ms */

  @Input() public stageID: string;
  @Input() public previewType: string;

  @ViewChild('canvas') public canvas: ElementRef;
  @ViewChild('spinner') public spinner: ElementRef;

  private renderer: any;
  private designStepId: number;
  private spinnerActiveSince: number|undefined = undefined;

  private subscriptions: Subscription = new Subscription();
  private templateChangedSubscription:
      AsyncEventEmitterSubscriber<TemplateConfig>;
  private stageConfigAppliedSubscription:
      AsyncEventEmitterSubscriber<boolean>;

  constructor(
      private stageService: StageService,
      private toolboxService: ToolboxService,
      private designStepService: DesignStepService,
      private placeholderService: PlaceholderService,
      private imageStoreService: ImageStoreService,
  )
  {
    this.designStepId = this.designStepService.getDesignStepId();

    const scheduleUpdate = async () => {
      if (this.renderer &&
          this.designStepService.getDesignStepId() === this.designStepId) {
        this.renderer.scheduleUpdate();
      }
    };

    this.stageConfigAppliedSubscription =
        this.stageService.stageConfigApplied.subscribe(async () => {
          if (this.renderer) {
            await this.renderer.updateSVGReference(this.stageID);
          }
        });
    this.subscriptions.add(
        this.toolboxService.placeholderChanged.subscribe(() => {
          if (this.stageService.getIsStageLoaded()) {
            scheduleUpdate();
          }
        }));
    this.subscriptions.add(
        this.placeholderService.placeholderChanged.subscribe(() => {
          if (this.stageService.getIsStageLoaded()) {
            scheduleUpdate();
          }
        }));
    this.templateChangedSubscription =
        this.designStepService.templateChanged.subscribe(async () => {
          if (this.shouldUseExternalRenderer) {
            this.showSpinner();
          }
        });

    if (!Utils.isSmallDevice()) {
      PreviewComponent.PREVIEW_OPTIONS.grabMoveEnabled = true;
    }
  }

  public ngAfterViewInit() {
    this.restartRenderer();
  }

  public async ngOnDestroy(): Promise<void>
  {
    this.subscriptions.unsubscribe();
    this.designStepService.templateChanged.unsubscribe(
        this.templateChangedSubscription);
    this.stageService.stageConfigApplied.unsubscribe(
        this.stageConfigAppliedSubscription);

    await this.removeCurrentRenderer();
  }

  private async restartRenderer(): Promise<void>
  {
    this.showSpinner();
    while (!this.stageID) {
      await Utils.sleep(PreviewComponent.TRY_RESTART_TIMEOUT);
      return await this.restartRenderer();
    }

    try {
      const rendererClass = PreviewComponent.RENDERER_CLASSES[this.previewType];
      if (!rendererClass) {
        throw new Error(`renderer for ${this.previewType} not found`);
      }
      this.renderer = new rendererClass(
          this.canvas.nativeElement, this.stageID, this.previewOptions);
    }
    catch (exc) {
      console.warn('creation of preview renderer failed: ' + exc +
                   ', needs retry');
      await Utils.sleep(PreviewComponent.TRY_RESTART_TIMEOUT);
      return await this.restartRenderer();
    }

    this.subscriptions.add(
      this.renderer.loaded.subscribe(loaded => {
        if (loaded) {
          this.hideSpinner();
        }
      }));
  }

  private showSpinner(): void
  {
    this.spinnerActiveSince = Date.now();
    this.spinner.nativeElement.style.display = 'inherit';
    this.canvas.nativeElement.style.opacity = 0.5;
    setTimeout(() => {
      if (this.spinnerActiveSince &&
          this.spinnerActiveSince + PreviewComponent.MAX_SPINNER_TIME
          <= Date.now()) {
        this.hideSpinner();
      }
    }, PreviewComponent.MAX_SPINNER_TIME);
  }

  private hideSpinner(): void
  {
    this.spinnerActiveSince = undefined;
    this.spinner.nativeElement.style.display = 'none';
    this.canvas.nativeElement.style.opacity = 1;
  }

  private get previewOptions(): any
  {
    return Object.assign({
      svgRenderer: this.shouldUseExternalRenderer
          ? this.imageStoreService.renderSvg.bind(this.imageStoreService)
          : undefined,
    }, PreviewComponent.PREVIEW_OPTIONS);
  }

  private get shouldUseExternalRenderer(): boolean
  {
    return Utils.isIphone() || Utils.isSafari();
  }

  private async removeCurrentRenderer(): Promise<void>
  {
    if (!this.renderer) {
      return;
    }

    try {
      await this.renderer.finalize();
    } catch (exc) {
      console.warn('could not shut down the renderer properly');
    }

    this.renderer = undefined;
  }
}
